<template>
  <v-container fluid class="max-width-container pa-0 py-6">
    <v-row justify="center" class="gap-y-6">

      <!-- LEFT COLUMN: Configuration Summary -->
      <v-col cols="12" md="5">
        <ConfigurationSummary
          :copier="copier"
          :cart-total-monthly-price="cartTotalMonthlyPrice"
          @remove-item="$emit('remove-item', $event)"
        />
      </v-col>

      <!-- RIGHT COLUMN: Contact Form -->
      <v-col cols="12" md="7">
        <v-card class="pa-8 border-card shadow-form h-100" rounded="xl" style="background: white;">
          <!-- Header and Title -->
          <div class="text-left mb-6">
            <h2 class="text-h5 font-weight-black text-blue-grey-darken-4 mb-2">Where should we send your quote?</h2>
            <p class="text-body-2 text-grey-darken-1 mb-0">
              We will email and text your detailed equipment quote. No spam.
            </p>
          </div>

          <v-form ref="contact_form" v-model="contactFormValid" @submit.prevent="ProceedToInstallation">

            <!-- 1-Click Google Quick Details Capture -->
            <div class="autofill-container mb-5">
              <button
                type="button"
                class="btn-autofill"
                :class="{ active: is_google_autofilled, loading: is_autofill_loading }"
                @click="TriggerGoogleAutofill"
                :disabled="is_autofill_loading"
              >
                <svg width="18" height="18" viewBox="0 0 48 48">
                  <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                  <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                  <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                  <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
                </svg>
                <span>{{ is_google_autofilled ? 'Autofilled via Google' : 'Autofill with Google' }}</span>
              </button>

              <div v-if="is_google_autofilled" class="autofill-status">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
                <span>Autofilled via Google Account ({{ form.contact.email }})</span>
              </div>
            </div>

            <div class="divider mb-6">
              <span>or enter contact details manually</span>
            </div>

            <!-- Contract Status Question Toggle -->
            <div class="mb-6">
              <span class="input-label font-weight-black text-grey-darken-2 d-block mb-2">
                DO YOU CURRENTLY HAVE AN ACTIVE CONTRACT ON YOUR COPIER?
              </span>
              <v-row dense>
                <v-col cols="12" sm="6">
                  <div
                    class="contract-status-card pa-4 rounded-lg cursor-pointer d-flex align-center h-100 transition-all"
                    :class="{ 'card-active': form.contract_status === 'out_of_contract' }"
                    @click="form.contract_status = 'out_of_contract'"
                  >
                    <v-icon
                      :icon="form.contract_status === 'out_of_contract' ? 'mdi-radiobox-marked' : 'mdi-radiobox-blank'"
                      :color="form.contract_status === 'out_of_contract' ? 'primary' : 'grey-darken-1'"
                      size="22"
                      class="me-3 flex-shrink-0"
                    />
                    <div class="flex-grow-1">
                      <div class="font-weight-black text-slate-800 text-body-2 mb-1">No Active Contract</div>
                      <div class="text-caption text-slate-500 font-weight-medium line-height-tight">New equipment / Out of contract</div>
                    </div>
                  </div>
                </v-col>

                <v-col cols="12" sm="6">
                  <div
                    class="contract-status-card pa-4 rounded-lg cursor-pointer d-flex align-center h-100 transition-all"
                    :class="{ 'card-active': form.contract_status === 'active' }"
                    @click="form.contract_status = 'active'"
                  >
                    <v-icon
                      :icon="form.contract_status === 'active' ? 'mdi-radiobox-marked' : 'mdi-radiobox-blank'"
                      :color="form.contract_status === 'active' ? 'primary' : 'grey-darken-1'"
                      size="22"
                      class="me-3 flex-shrink-0"
                    />
                    <div class="flex-grow-1">
                      <div class="font-weight-black text-slate-800 text-body-2 mb-1">Yes, Active Contract</div>
                      <div class="text-caption text-slate-500 font-weight-medium line-height-tight">Looking to upgrade or service</div>
                    </div>
                  </div>
                </v-col>
              </v-row>
            </div>

            <v-row class="gap-y-4">
              <v-col cols="12">
                <span class="input-label font-weight-black text-grey-darken-2 d-block mb-1">LEGAL BUSINESS NAME</span>
                <v-text-field
                  v-model="form.contact.business_name"
                  placeholder="e.g. Northgate Dental Group LLC"
                  variant="outlined"
                  density="comfortable"
                  rounded="lg"
                  color="primary"
                  hide-details
                  :rules="[rules.required]"
                />
              </v-col>

              <v-col cols="12" sm="6">
                <span class="input-label font-weight-black text-grey-darken-2 d-block mb-1">FIRST NAME</span>
                <v-text-field
                  v-model="form.contact.first_name"
                  placeholder="e.g. Maria"
                  variant="outlined"
                  density="comfortable"
                  rounded="lg"
                  color="primary"
                  hide-details
                  autocomplete="given-name"
                  :class="{ 'autofilled-field': is_field_pulsing }"
                  :rules="[rules.required]"
                />
              </v-col>

              <v-col cols="12" sm="6">
                <span class="input-label font-weight-black text-grey-darken-2 d-block mb-1">LAST NAME</span>
                <v-text-field
                  v-model="form.contact.last_name"
                  placeholder="e.g. Reyes"
                  variant="outlined"
                  density="comfortable"
                  rounded="lg"
                  color="primary"
                  hide-details
                  autocomplete="family-name"
                  :class="{ 'autofilled-field': is_field_pulsing }"
                  :rules="[rules.required]"
                />
              </v-col>

              <v-col cols="12" sm="6">
                <span class="input-label font-weight-black text-grey-darken-2 d-block mb-1">WORK EMAIL</span>
                <v-text-field
                  v-model="form.contact.email"
                  placeholder="e.g. maria@northgate.com"
                  variant="outlined"
                  density="comfortable"
                  rounded="lg"
                  color="primary"
                  hide-details
                  autocomplete="email"
                  :class="{ 'autofilled-field': is_field_pulsing }"
                  :rules="[rules.required, rules.email]"
                />
              </v-col>

              <v-col cols="12" sm="6">
                <span class="input-label font-weight-black text-grey-darken-2 d-block mb-1">MOBILE NUMBER</span>
                <v-text-field
                  v-model="form.contact.phone"
                  placeholder="e.g. (813) 555-0140"
                  variant="outlined"
                  density="comfortable"
                  rounded="lg"
                  color="primary"
                  hide-details
                  autocomplete="tel"
                  :rules="[rules.required]"
                />
              </v-col>
            </v-row>

            <v-btn
              type="submit"
              :color="ConfigElementsColor"
              block
              rounded="lg"
              size="large"
              elevation="0"
              class="text-none font-weight-bold cta-btn mt-6 mb-2 d-flex align-center justify-center"
            >
              Continue to Installation Address
              <v-icon icon="mdi-arrow-right" class="ml-2" size="18" />
            </v-btn>

            <p class="dealer-acknowledgment text-center mb-3">
              By submitting this request, you acknowledge that 1-800 Office Solutions may share your information with one independent dealer from its selected network to respond to your inquiry. Participating dealers may pay 1-800 Office Solutions for access to these opportunities. See our <a href="https://1800officesolutions.com/terms-and-conditions/" target="_blank" rel="noopener">Terms & Conditions</a> and <a href="https://1800officesolutions.com/privacy-policy/" target="_blank" rel="noopener">Privacy Policy</a>.
            </p>

            <v-btn
              variant="outlined"
              color="primary"
              block
              rounded="lg"
              size="large"
              class="text-none font-weight-bold"
              @click="configurator_step = 'configurator'"
            >
              Adjust my configuration
            </v-btn>
          </v-form>
        </v-card>
      </v-col>

    </v-row>
  </v-container>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { storeToRefs } from 'pinia'
import { useGlobalsStore } from '@/stores/globals'
import ConfigurationSummary from '@/components/configurator/ConfigurationSummary.vue'

defineProps<{
  copier?: {
    instance: any
    datasets: any[]
    accessories: any[]
  }
  cartTotalMonthlyPrice?: number
}>()

const emit = defineEmits<{
  (e: 'remove-item', idx: number): void
  (e: 'proceed'): void
}>()

const store = useGlobalsStore()
const { form, configurator_step, ConfigElementsColor } = storeToRefs(store)

const contact_form = ref<any>(null)
const contactFormValid = ref(false)
const is_google_autofilled = ref(false)
const is_autofill_loading = ref(false)
const is_field_pulsing = ref(false)

const rules = {
  required: (v: any) => !!v || 'Field is required',
  email: (v: string) => /.+@.+\..+/.test(v) || 'E-mail must be valid'
}

async function ProceedToInstallation() {
  if (contact_form.value) {
    const { valid } = await contact_form.value.validate()
    if (!valid) return
  }
  configurator_step.value = 'summary'
  emit('proceed')
}

function ApplyGoogleData(data: { first_name: string; last_name: string; email: string }) {
  form.value.contact.first_name = data.first_name
  form.value.contact.last_name = data.last_name
  form.value.contact.email = data.email
  is_google_autofilled.value = true

  is_field_pulsing.value = true
  setTimeout(() => {
    is_field_pulsing.value = false
  }, 1400)
}

function GetBridgeUrl(): string {
  const base_url =
    (window as any).catalog_config?.catalog_server_url ||
    import.meta.env.VITE_SERVERAPI_URL ||
    'https://catalogv2.1800os.com'

  // Map .test to .localtest.me so Google Cloud Console accepts the origin in local dev
  const bridge_host = base_url.includes('.test')
    ? base_url.replace('.test', '.localtest.me').replace('https://', 'http://')
    : base_url

  return `${bridge_host.replace(/\/+$/, '')}/auth/google-bridge`
}

function TriggerGoogleAutofill() {
  is_autofill_loading.value = true

  const bridge_url = `${GetBridgeUrl()}?origin=${encodeURIComponent(window.location.origin)}`
  const width = 480
  const height = 640
  const left = window.screenX + (window.outerWidth - width) / 2
  const top = window.screenY + (window.outerHeight - height) / 2

  const popup = window.open(
    bridge_url,
    'google_autofill_bridge',
    `width=${width},height=${height},left=${left},top=${top},status=no,menubar=no,toolbar=no`
  )

  if (!popup) {
    console.warn('[Showroom] Popup was blocked by browser')
    is_autofill_loading.value = false
    return
  }

  const MessageHandler = (event: MessageEvent) => {
    if (event.data?.type === 'GOOGLE_AUTOFILL_SUCCESS' && event.data?.user) {
      window.removeEventListener('message', MessageHandler)
      clearInterval(popup_poll)
      is_autofill_loading.value = false

      ApplyGoogleData({
        first_name: event.data.user.first_name || '',
        last_name: event.data.user.last_name || '',
        email: event.data.user.email || ''
      })
    }
  }

  window.addEventListener('message', MessageHandler)

  const popup_poll = setInterval(() => {
    if (!popup || popup.closed) {
      clearInterval(popup_poll)
      window.removeEventListener('message', MessageHandler)
      is_autofill_loading.value = false
    }
  }, 500)
}
</script>

<style scoped lang="scss">
.max-width-container {
  max-width: 1200px;
}
.border-card {
  border: 1px solid rgba(148, 163, 184, 0.2) !important;
}
.shadow-form {
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.05);
}
.contract-status-card {
  border: 1px solid rgba(148, 163, 184, 0.25);
  background-color: #f8fafc;
  transition: all 0.2s ease-in-out;

  &:hover {
    border-color: #3b82f6;
    background-color: #ffffff;
  }

  &.card-active {
    border-color: #2563eb !important;
    background-color: #eff6ff !important;
    box-shadow: 0 2px 6px rgba(37, 99, 235, 0.08);
  }
}
.input-label {
  font-size: 0.72rem;
  letter-spacing: 0.5px;
}
.text-slate-800 {
  color: #1e293b;
}
.text-slate-500 {
  color: #64748b;
}
.line-height-tight {
  line-height: 1.25;
}

/* 1-Click Google Autofill Styles */
.autofill-container {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.btn-autofill {
  width: 100%;
  background: #ffffff;
  color: #374151;
  border: 1px solid #d1d5db;
  padding: 0.75rem 1rem;
  font-size: 0.95rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.65rem;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: #f9fafb;
    border-color: #9ca3af;
    transform: translateY(-1px);
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
  }

  &.active {
    border-color: #10b981;
    background: #f0fdf4;
    color: #065f46;
  }

  &:disabled {
    opacity: 0.7;
    cursor: wait;
  }
}

.autofill-status {
  font-size: 0.82rem;
  color: #15803d;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
}

.divider {
  display: flex;
  align-items: center;
  text-align: center;
  color: #6b7280;
  font-size: 0.85rem;

  &::before,
  &::after {
    content: '';
    flex: 1;
    border-bottom: 1px solid #e5e7eb;
  }

  span {
    padding: 0 0.85rem;
  }
}

.autofilled-field :deep(.v-field) {
  animation: highlightPulse 1.4s ease;
  border-color: #86efac !important;
  background-color: #f0fdf4 !important;
}

.dealer-acknowledgment {
  font-size: 11px;
  line-height: 1.4;
  color: #64748b;
  margin-top: 8px;

  a {
    color: #2563eb;
    text-decoration: underline;
  }
}

@keyframes highlightPulse {
  0% {
    background-color: #dcfce7 !important;
    transform: scale(1.01);
  }
  50% {
    background-color: #f0fdf4 !important;
  }
  100% {
    background-color: inherit;
    transform: scale(1);
  }
}
</style>
